import React, { useState, useEffect, useMemo } from 'react';
import { resolveEffectiveTenant, isAccountExcludedFromPlaybooks } from '@/lib/tenantUtils';
import { hasPreviewNoExecutionEligible } from '@/lib/previewNoExecEligibleSession';
import { AccountFinal } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { applyTenantOverrides } from '@/components/utils/tenantOverrides';
import { Input } from '@/components/ui/input';
import { Search, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export default function AccountSelector({ onSelect, currentTenant, eligibleAccounts, playbookKey }) {
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const effectiveCurrentTenant = currentTenant || contextTenant;

  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const getFitScore = (account) => {
    const raw =
      account?.account_fit_score ??
      account?.fit_score ??
      account?.accountFitScore;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  useEffect(() => {
    // If parent supplied eligible accounts, always trust that as source of truth
    if (eligibleAccounts !== undefined) {
      const tenantId = contextUser
        ? resolveEffectiveTenant(
            contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
            effectiveCurrentTenant,
            contextUser,
            'instructure'
          )
        : null;
      const nextAccounts = (eligibleAccounts || [])
        .map((a) => (tenantId ? applyTenantOverrides(a, tenantId) : a))
        .filter((a) => {
          if (!tenantId) return a?.is_visible !== false;
          return a?.is_visible !== false && !isAccountExcludedFromPlaybooks(tenantId, a);
        });
      setAccounts(nextAccounts);
      setIsLoading(false);
      return;
    }

    // Only fall back to loading all accounts if prop was not supplied at all
    loadAccounts();
  }, [currentTenant, eligibleAccounts, playbookKey, contextUser]);

  const loadAccounts = async () => {
    if (!contextUser) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const tenantId = resolveEffectiveTenant(
        contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
        effectiveCurrentTenant,
        contextUser,
        'instructure'
      );

      const allAccounts = await AccountFinal.list('-account_fit_score');

      const tenantMatches = (record) => {
        if (!record) return false;
        if (tenantId === 'smowl') return record.tenant_id === 'smowl';
        if (record.tenant_id === tenantId || record.tenant_id === 'global') return true;
        if (tenantId === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id || record.tenant_id === 'clairelabs')) return true;
        if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(tenantId)) return true;
        if (record.tenant_overrides && record.tenant_overrides[tenantId]) return true;
        if (tenantId === 'diambra' && (record.tenant_id === 'smowl' || record.tenant_id === 'instructure' || record.tenant_id === 'clairelabs' || record.tenant_id === 'base44' || record.tenant_id === 'default')) return true;
        if (tenantId === 'edqwest') return record.tenant_id === 'edqwest' || record.tenant_id === 'global';
        if (tenantId === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
        return false;
      };

      const fallbackAccounts = allAccounts
        .filter(tenantMatches)
        .map((acc) => applyTenantOverrides(acc, tenantId))
        .filter((a) => a.is_visible !== false && !isAccountExcludedFromPlaybooks(tenantId, a));

      setAccounts(fallbackAccounts);
    } catch (error) {
      console.error('Error loading accounts:', error);
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const tenantIdForPreviewFlag = useMemo(
    () =>
      contextUser
        ? resolveEffectiveTenant(
            contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
            effectiveCurrentTenant,
            contextUser,
            'instructure'
          )
        : null,
    [contextUser, effectiveCurrentTenant]
  );

  const filteredAccounts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const result = (accounts || []).filter(account => {
      if (!term) return true;

      return (
        account.account_name?.toLowerCase().includes(term) ||
        account.country?.toLowerCase().includes(term)
      );
    });

    return result.sort((a, b) => {
      const aScore = getFitScore(a);
      const bScore = getFitScore(b);
      const aValid = Number.isFinite(aScore);
      const bValid = Number.isFinite(bScore);
      if (aValid && bValid && aScore !== bScore) return bScore - aScore;
      if (aValid && !bValid) return -1;
      if (!aValid && bValid) return 1;
      return 0;
    });
  }, [accounts, searchTerm, playbookKey]);

  const accountsToRender = filteredAccounts.slice(0, 50);

  const handleAccountClick = (account) => {
    if (onSelect) {
      onSelect(account);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C2C2]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search accounts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {accountsToRender.map(account => (
          <button
            key={account.account_id || account.id}
            onClick={() => handleAccountClick(account)}
            className="w-full text-left p-4 border rounded-lg hover:border-[#00C2C2] hover:bg-[#00C2C2]/5 transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Building2 className="w-5 h-5 text-[#00C2C2] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-[#0A1A2F] truncate">
                    {account.account_name}
                  </h4>
                  <p className="text-sm text-[#5C6B7A]">{account.country || ''}</p>
                </div>
              </div>
              {(() => {
                const accountId = account.account_id || account.id;
                const previewAuthoritativeEmpty =
                  playbookKey &&
                  tenantIdForPreviewFlag &&
                  hasPreviewNoExecutionEligible(tenantIdForPreviewFlag, playbookKey, accountId);
                const eligibleCountLabel = account.eligible_count_unknown
                  ? 'Unknown'
                  : previewAuthoritativeEmpty
                    ? '0'
                    : (account.eligible_total != null && account.eligible_total !== account.eligible_remaining)
                      ? `${account.eligible_remaining ?? 0} / ${account.eligible_total}`
                      : (account.eligible_remaining ?? 0);
                const showEligibleBadge = account.eligible_remaining != null || account.eligible_count_unknown;
                const fitScore = getFitScore(account);
                const showFitBadge = fitScore != null;
                return (showEligibleBadge || showFitBadge) ? (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {showFitBadge && (
                      <Badge variant="outline" className="flex-shrink-0">
                        Fit: {fitScore}
                      </Badge>
                    )}
                    {showEligibleBadge && (
                      <Badge
                        variant="outline"
                        className="flex-shrink-0"
                        title={
                          previewAuthoritativeEmpty && !account.eligible_count_unknown
                            ? 'Playbook Preview found no execution-eligible personas for this account; the server list count may be out of date.'
                            : undefined
                        }
                      >
                        Eligible: {eligibleCountLabel}
                      </Badge>
                    )}
                  </div>
                ) : null;
              })()}
            </div>
          </button>
        ))}
      </div>

      {filteredAccounts.length === 0 && (
        <div className="text-center py-8 text-[#5C6B7A]">
          No accounts found
        </div>
      )}
    </div>
  );
}
